import { computed, inject, Injectable, signal } from '@angular/core';
import { CurrentUserDto } from '@app/data/dto';
import { CurrentUserService } from './current-user.service';

export interface CurrentCompany {
  readonly companyId: string;
  readonly companyName: string;
}

@Injectable({ providedIn: 'root' })
export class CurrentCompanyService {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly currentCompanySignal = signal<CurrentCompany | null>(null);
  private readonly ownCompanySignal = signal<CurrentCompany | null>(null);
  private ownCompanyPromise: Promise<CurrentCompany | null> | null = null;

  readonly currentCompany = this.currentCompanySignal.asReadonly();
  readonly ownCompany = this.ownCompanySignal.asReadonly();
  readonly isExternalCompany = computed(() => {
    const currentCompany = this.currentCompanySignal();
    const ownCompany = this.ownCompanySignal();
    return !!currentCompany && !!ownCompany && currentCompany.companyId !== ownCompany.companyId;
  });

  async getCurrentCompany(forceRefresh = false): Promise<CurrentCompany | null> {
    await this.ensureOwnCompanyLoaded(forceRefresh);

    if (!this.currentCompanySignal()) {
      this.currentCompanySignal.set(this.ownCompanySignal());
    }

    return this.currentCompanySignal();
  }

  async resetToCurrentUser(): Promise<CurrentCompany | null> {
    await this.ensureOwnCompanyLoaded(true);
    this.currentCompanySignal.set(this.ownCompanySignal());
    return this.currentCompanySignal();
  }

  setCurrentCompany(company: CurrentCompany): void {
    this.currentCompanySignal.set({
      companyId: company.companyId,
      companyName: company.companyName,
    });
  }

  private async ensureOwnCompanyLoaded(forceRefresh = false): Promise<CurrentCompany | null> {
    if (!forceRefresh && this.ownCompanySignal()) {
      return this.ownCompanySignal();
    }

    if (this.ownCompanyPromise !== null) {
      return this.ownCompanyPromise;
    }

    this.ownCompanyPromise = (async () => {
      try {
        const currentUser = await this.currentUserService.getCurrentUser(forceRefresh);
        const company = this.toCurrentCompany(currentUser);
        this.ownCompanySignal.set(company);
        return company;
      } finally {
        this.ownCompanyPromise = null;
      }
    })();

    return this.ownCompanyPromise;
  }

  private toCurrentCompany(currentUser: CurrentUserDto | null): CurrentCompany | null {
    const appUser = currentUser?.appUser;
    const company = appUser?.company;
    const companyId = company?.companyId?.trim();
    if (!companyId) {
      return null;
    }

    const companyName = company?.companyName?.trim() || company?.name?.trim() || appUser?.companyName?.trim() || '-';
    return {
      companyId,
      companyName,
    };
  }
}